import type { RecipeFilterItemType } from "@features/recipe/recipe-filter/recipe-filter-store";
import {
  getRecipeFilters,
  RecipeCategoryEnum,
  useRecipeFilters,
} from "@features/recipe/recipe-filter/recipe-filter-store";
import { useInfiniteQuery } from "@tanstack/react-query";
import api, { methods } from "@api";
import type { Recipe, RecipesPaginationResponse } from "@type/recipe";
import * as React from "react";
import { queryClient } from "@provider/query-client";
import { flattenPages } from "@utils";

type RecipeParams = {
  diet_ids: number[];
  kitchen_ids: number[];
  varity_ids: number[];
  moment_ids: number[];
  season_ids: number[];
  page: number;
  search?: string;
};

const getQueryFn = (params: RecipeParams) =>
  api<RecipesPaginationResponse>({
    path: "/recepten",
    method: methods.GET,
    params,
  });

function getQueryKey(params: RecipeParams) {
  const arrayOfValues = Object.values(params || {});
  return ["recipes", ...arrayOfValues];
}

let cachedRecipes: Recipe[] = [];
export function getRecipeFromCache(id: number) {
  return cachedRecipes.find((r) => r.id === id);
}

function filtersToParams({
  filters,
  search,
  page,
}: {
  filters: RecipeFilterItemType[];
  search: string | undefined;
  page: number;
}) {
  const params: RecipeParams = {
    diet_ids: filters.filter((f) => f.category === RecipeCategoryEnum.DIET).map((f) => f.id),
    kitchen_ids: filters.filter((f) => f.category === RecipeCategoryEnum.KITCHEN).map((f) => f.id),
    varity_ids: filters.filter((f) => f.category === RecipeCategoryEnum.VARITY).map((f) => f.id),
    moment_ids: filters.filter((f) => f.category === RecipeCategoryEnum.MOMENT).map((f) => f.id),
    season_ids: filters.filter((f) => f.category === RecipeCategoryEnum.SEASON).map((f) => f.id),
    page,
  };

  if (search) {
    params.search = search;
  }
  return params;
}

export function prefetchRecipes() {
  const search: string | undefined = undefined;
  const filters = getRecipeFilters();
  const params = filtersToParams({ filters, search, page: 1 });

  queryClient.prefetchInfiniteQuery({
    queryKey: getQueryKey(params),
    queryFn: () => getQueryFn(params),
    initialPageParam: 1,
    // 30 seconds
    staleTime: 30 * 1000,
  });
}
export function useRecipes({ search }: { search?: string } | undefined) {
  const filters = useRecipeFilters();
  const params = React.useMemo(() => filtersToParams({ filters, search, page: 1 }), [filters, search]);
  const query = useInfiniteQuery({
    queryKey: getQueryKey(params),
    queryFn: ({ pageParam }) => {
      const pageParams = {
        ...params,
        page: pageParam,
      };
      return getQueryFn(pageParams);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage?.next_page_url?.split("page=")[1];
      return page ? Number(page) : undefined;
    },
  });

  const recipes = React.useMemo(() => flattenPages(query.data), [query.data]);
  cachedRecipes = recipes;
  return { recipes, ...query };
}
